<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { ValidData as PhotosFormValidData } from '@/components/forms/DetailedJobAttachmentsForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';

import DetailedJobPhotosDescList from '@/components/descLists/DetailedJobPhotosDescList.vue';
import DetailedJobAttachmentsForm from '@/components/forms/DetailedJobAttachmentsForm.vue';
import JobEditAccordionSection from '@/components/sections/JobEditAccordionSection.vue';

export interface Props {
  stopImages?: ReadonlyDeep<openapi.GetStopImagesContentOpenapi>;
  deleteImage?: (stopNum: string | number, photoIndex: number) => Promise<void>;
  isLoadingPhotos?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  stopImages: undefined,
  deleteImage: undefined,
  isLoadingPhotos: false,
});

const emit = defineEmits<{
  submitPhotos: [formData: PhotosFormValidData, cb: () => void];
}>();

const hasAnyPhotos = computed(() => {
  if (!props.stopImages) return false;
  return Object.values(props.stopImages).some((photos) => photos.length > 0);
});

const totalPhotosCount = computed(() => {
  if (!props.stopImages) return 0;
  return Object.values(props.stopImages).reduce(
    (sum, photos) => sum + photos.length,
    0,
  );
});

function getDeleteImageHandler(stopNum: string | number) {
  if (!props.deleteImage) return undefined;
  const deleteImageFn = props.deleteImage;
  return (photoIndex: number) => deleteImageFn(stopNum, photoIndex);
}
</script>

<template>
  <JobEditAccordionSection
    :class="$style.jobEditPhotosSection"
    title="IMAGES"
    :initially-collapsed="true"
    data-testid="jobEditPhotosSection"
  >
    <template v-if="totalPhotosCount" #collapsedContent>
      <span
        >{{ totalPhotosCount }}
        {{ totalPhotosCount === 1 ? 'Attachment' : 'Attachments' }}</span
      >
    </template>
    <template v-if="hasAnyPhotos && props.stopImages">
      <div
        v-for="(photos, stopNum) in props.stopImages"
        :key="stopNum"
        :class="$style.stopBlock"
      >
        <template v-if="photos.length">
          <h4 :class="$style.stopTitle">Stop {{ stopNum }}</h4>
          <DetailedJobPhotosDescList
            :photos="photos"
            :delete-image="getDeleteImageHandler(stopNum)"
            :class="$style.photosList"
          />
        </template>
      </div>
    </template>
    <div v-else :class="$style.emptyState"> No photos available </div>

    <DetailedJobAttachmentsForm
      :loading="props.isLoadingPhotos"
      drop-zone-variant="dropzone"
      :class="$style.uploadForm"
      @submit="(formData, cb) => emit('submitPhotos', formData, cb)"
    />
  </JobEditAccordionSection>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobEditPhotosSection {
  max-width: 100%;
}

.stopBlock {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.stopTitle {
  font-weight: 600;
  font-size: 14px;
  color: colors.$color-text-primary;
  margin: 0 0 12px 0;
}

.photosList {
  justify-content: flex-start;
}

.emptyState {
  font-size: 14px;
  color: colors.$color-text-tertiary;
}

.uploadForm {
  margin-top: 32px;
  max-width: 600px;
}
</style>
